import React, { useEffect, useState } from "react";
import "./AccountLedger.css";

const API_URL = "https://server.tradefxonline.com/accountledger.php";
const BASE_IMAGE_URL = "https://server.tradefxonline.com";

const AccountLedger = () => {
  const [summary, setSummary] = useState({
    deposits: 0,
    withdrawals: 0,
    deposit_count: 0,
    withdrawal_count: 0,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    clientName: "",
    currentIndex: 0,
    images: []
  });

  useEffect(() => {
    const fetchLedger = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(API_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        setSummary(json?.summary ?? { deposits: 0, withdrawals: 0, deposit_count: 0, withdrawal_count: 0 });
        setRows(Array.isArray(json?.data) ? json.data : []);
      } catch (e) {
        setError(e?.message || "Failed to load");
        setSummary({ deposits: 0, withdrawals: 0, deposit_count: 0, withdrawal_count: 0 });
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, []);

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : n;

  const openImageModal = (proofs, clientName, index = 0) => {
    if (!proofs || proofs.length === 0) return;
    
    const images = Array.isArray(proofs) ? proofs : [proofs];
    const fullImageUrls = images.map(proof => `${BASE_IMAGE_URL}${proof}`);
    
    setImageModal({
      isOpen: true,
      imageUrl: fullImageUrls[index],
      clientName: clientName,
      currentIndex: index,
      images: fullImageUrls
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: "",
      clientName: "",
      currentIndex: 0,
      images: []
    });
  };

  const nextImage = () => {
    const nextIndex = (imageModal.currentIndex + 1) % imageModal.images.length;
    setImageModal(prev => ({
      ...prev,
      currentIndex: nextIndex,
      imageUrl: prev.images[nextIndex]
    }));
  };

  const prevImage = () => {
    const prevIndex = imageModal.currentIndex === 0 
      ? imageModal.images.length - 1 
      : imageModal.currentIndex - 1;
    setImageModal(prev => ({
      ...prev,
      currentIndex: prevIndex,
      imageUrl: prev.images[prevIndex]
    }));
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return "Pending";
      case 2: return "Approved";
      case 3: return "Rejected";
      default: return "Unknown";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 1: return "status-pending";
      case 2: return "status-approved";
      case 3: return "status-rejected";
      default: return "status-unknown";
    }
  };

  return (
    <div className="dashboard-container">
      <main className="main-content">
        {/* Header Section */}
        <div className="header-row">
          <div>
            <h2 className="page-title">Account Ledger</h2>
            <p className="subtitle">View All Ledger Transactions</p>
          </div>
          <div className="breadcrumb">
            <span className="breadcrumb-link">Home</span>
            <span className="breadcrumb-separator">/</span>
            <span>Ledger</span>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="stats-container">
          <div className="stat-box">
            <p>Deposits</p>
            <h3>{fmt(summary.deposits)}</h3>
          </div>
          <div className="stat-box">
            <p>Withdrawals</p>
            <h3>{fmt(summary.withdrawals)}</h3>
          </div>
          <div className="stat-box">
            <p>Deposit Count</p>
            <h3>{fmt(summary.deposit_count)}</h3>
          </div>
          <div className="stat-box">
            <p>Withdrawal Count</p>
            <h3>{fmt(summary.withdrawal_count)}</h3>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date & Time</th>
                <th>Client</th>
                <th>Trading Account #</th>
                <th>Notes</th>
                <th>Deposit</th>
                <th>Withdraw</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Transaction Proof</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", color: "crimson", padding: "20px" }}>
                    Error: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>
                    No data available
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id ?? idx}>
                    <td>{idx + 1}</td>
                    <td>{r.datetime}</td>
                    <td>{r.client || "-"}</td>
                    <td>{r.trade_id || "-"}</td>
                    <td>{r.notes || "-"}</td>
                    <td style={{ color: r.deposit > 0 ? "#28a745" : "#6c757d" }}>
                      {r.deposit > 0 ? `+${fmt(r.deposit)}` : "-"}
                    </td>
                    <td style={{ color: r.withdraw > 0 ? "#dc3545" : "#6c757d" }}>
                      {r.withdraw > 0 ? `-${fmt(r.withdraw)}` : "-"}
                    </td>
                    <td>{r.payment_method || "-"}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(r.status)}`}>
                        {getStatusText(r.status)}
                      </span>
                    </td>
                    <td>
                      {r.transaction_proof && r.transaction_proof.length > 0 ? (
                        <button 
                          className="view-proof-btn"
                          onClick={() => openImageModal(r.transaction_proof, r.client)}
                          title="View Transaction Proof"
                        >
                          View Proof ({Array.isArray(r.transaction_proof) ? r.transaction_proof.length : 1})
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Image Modal */}
        {imageModal.isOpen && (
          <div className="image-modal-overlay" onClick={closeImageModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="image-modal-header">
                <h3>Transaction Proof - {imageModal.clientName}</h3>
                <div className="image-modal-controls">
                  {imageModal.images.length > 1 && (
                    <span className="image-counter">
                      {imageModal.currentIndex + 1} of {imageModal.images.length}
                    </span>
                  )}
                  <button className="image-modal-close" onClick={closeImageModal}>
                    ×
                  </button>
                </div>
              </div>
              <div className="image-modal-body">
                {imageModal.images.length > 1 && (
                  <button className="image-nav-btn prev-btn" onClick={prevImage}>
                    ‹
                  </button>
                )}
                <img 
                  src={imageModal.imageUrl} 
                  alt="Transaction Proof" 
                  className="proof-image"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                  }}
                />
                {imageModal.images.length > 1 && (
                  <button className="image-nav-btn next-btn" onClick={nextImage}>
                    ›
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountLedger;
